import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { postizAPI } from "../api/postizAPI";

// ─── Platform colour mapping ────────────────────────────────────────────────
const PLATFORM_COLORS = {
  facebook:  { bg: "#1877f2", light: "#dbeafe" },
  instagram: { bg: "#e1306c", light: "#fce7f3" },
  twitter:   { bg: "#1da1f2", light: "#dbeafe" },
  x:         { bg: "#000000", light: "#f3f4f6" },
  linkedin:  { bg: "#0077b5", light: "#dbeafe" },
  tiktok:    { bg: "#010101", light: "#f3f4f6" },
  youtube:   { bg: "#ff0000", light: "#fee2e2" },
  pinterest: { bg: "#e60023", light: "#fee2e2" },
  default:   { bg: "#6366f1", light: "#ede9fe" },
};

const getPlatformColor = (platform = "") => {
  const key = platform.toLowerCase();
  return PLATFORM_COLORS[key] || PLATFORM_COLORS.default;
};

const PlatformIcon = ({ platform, size = 14 }) => {
  const icons = {
    facebook: "f",  instagram: "ig", twitter: "t",
    x: "𝕏",        linkedin: "in",  tiktok: "tt",
    youtube: "yt",  pinterest: "p",
  };
  const letter = icons[platform?.toLowerCase()] || platform?.[0]?.toUpperCase() || "?";
  const { bg } = getPlatformColor(platform);
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: size + 4, height: size + 4, borderRadius: "50%",
        background: bg, color: "#fff",
        fontSize: Math.max(8, size - 4), fontWeight: 700,
        flexShrink: 0, lineHeight: 1,
      }}
    >
      {letter}
    </span>
  );
};

// ─── PostChip ────────────────────────────────────────────────────────────────
const PostChip = ({ post, isDarkMode, onClick, onEdit, onDelete, isDeleting }) => {
  const [hovered, setHovered] = useState(false);
  const platform = post.platform || (post.platforms?.[0]) || "default";
  const { bg, light } = getPlatformColor(platform);
  const chipBg = isDarkMode ? bg + "33" : light;
  const borderColor = bg;
  const textColor = isDarkMode ? "#e5e7eb" : "#111827";

  return (
    <div
      style={{
        position: "relative",
        background: chipBg,
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: "0 4px 4px 0",
        padding: "2px 5px",
        cursor: "pointer",
        transition: "opacity 0.15s",
        opacity: isDeleting ? 0.5 : 1,
        userSelect: "none",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => !hovered && onClick && onClick(post)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <PlatformIcon platform={platform} size={10} />
        {post.status === "failed" && (
          <span title={post.errorMessage || "Failed to publish"} style={{ fontSize: 10, lineHeight: 1 }}>⚠️</span>
        )}
        <span
          style={{
            fontSize: 10, fontWeight: 500, color: textColor,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            maxWidth: 90, display: "block",
          }}
        >
          {post.title || post.body?.slice(0, 35) || "Post"}
        </span>
      </div>

      {hovered && (
        <div
          style={{
            position: "absolute", top: 0, right: 0, bottom: 0,
            display: "flex", alignItems: "center", gap: 2,
            paddingRight: 3, background: "inherit",
            borderRadius: "0 4px 4px 0",
          }}
          onClick={e => e.stopPropagation()}
        >
          {post.status === "scheduled" && (
            <button
              title="Edit"
              onClick={() => onEdit && onEdit(post)}
              style={{
                border: "none", cursor: "pointer", padding: "1px 4px",
                borderRadius: 3, background: isDarkMode ? "#1e40af" : "#bfdbfe",
                color: isDarkMode ? "#93c5fd" : "#1e40af",
                fontSize: 10, fontWeight: 600, lineHeight: 1.4,
              }}
            >✏️</button>
          )}
          <button
            title="Delete"
            disabled={isDeleting}
            onClick={() => onDelete && onDelete(post)}
            style={{
              border: "none", cursor: isDeleting ? "not-allowed" : "pointer",
              padding: "1px 4px", borderRadius: 3,
              background: isDarkMode ? "#7f1d1d" : "#fee2e2",
              color: isDarkMode ? "#fca5a5" : "#b91c1c",
              fontSize: 10, fontWeight: 600, lineHeight: 1.4,
            }}
          >{isDeleting ? "…" : "🗑️"}</button>
        </div>
      )}
    </div>
  );
};

// ─── CalendarCell ─────────────────────────────────────────────────────────────
const MAX_VISIBLE = 2;

const CalendarCell = ({
  day, posts, isCurrentMonth, isToday,
  isDarkMode, onPostClick, onEditPost, onDeletePost, deletingPostId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? posts : posts.slice(0, MAX_VISIBLE);
  const overflow = posts.length - MAX_VISIBLE;

  const cellBg = isToday
    ? (isDarkMode ? "#1e3a5f" : "#eff6ff")
    : isDarkMode ? "#1f2937" : "#ffffff";

  const dayColor = isToday
    ? "#ffffff"
    : isCurrentMonth
      ? (isDarkMode ? "#e5e7eb" : "#111827")
      : (isDarkMode ? "#4b5563" : "#d1d5db");

  return (
    <div
      style={{
        background: cellBg,
        border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
        borderRadius: 6,
        // ↓ SMALLER: was 110, now 72
        minHeight: 72,
        padding: "4px 4px 3px",
        display: "flex", flexDirection: "column", gap: 2,
        position: "relative",
        transition: "background 0.15s",
      }}
    >
      {/* Day number */}
      <span
        style={{
          fontSize: 11, fontWeight: isToday ? 700 : 500,
          alignSelf: "flex-end",
          background: isToday ? "#3b82f6" : "transparent",
          color: dayColor,
          width: 18, height: 18, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {day}
      </span>

      {/* Post chips */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
        {visible.map(post => (
          <PostChip
            key={post.id}
            post={post}
            isDarkMode={isDarkMode}
            onClick={onPostClick}
            onEdit={onEditPost}
            onDelete={onDeletePost}
            isDeleting={deletingPostId === post.id}
          />
        ))}
      </div>

      {!expanded && overflow > 0 && (
        <button
          onClick={() => setExpanded(true)}
          style={{
            border: "none", background: "none", cursor: "pointer",
            fontSize: 10, fontWeight: 600, textAlign: "left", padding: "0 2px",
            color: isDarkMode ? "#60a5fa" : "#3b82f6",
          }}
        >+{overflow} more</button>
      )}
      {expanded && posts.length > MAX_VISIBLE && (
        <button
          onClick={() => setExpanded(false)}
          style={{
            border: "none", background: "none", cursor: "pointer",
            fontSize: 10, fontWeight: 600, textAlign: "left", padding: "0 2px",
            color: isDarkMode ? "#9ca3af" : "#6b7280",
          }}
        >↑ Less</button>
      )}
    </div>
  );
};

// ─── PostDetailModal ──────────────────────────────────────────────────────────
const PostDetailModal = ({ post, isDarkMode, onClose, onEdit, onDelete, isDeleting }) => {
  if (!post) return null;
  const platform = post.platform || post.platforms?.[0] || "default";
  const { bg } = getPlatformColor(platform);
  const scheduledDate = new Date(post.scheduledDate || post.createdAt);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: isDarkMode ? "#1f2937" : "#ffffff",
          borderRadius: 14, width: "100%", maxWidth: 440,
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
          overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ background: bg, height: 5 }} />
        <div style={{ padding: "20px 24px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <PlatformIcon platform={platform} size={20} />
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: isDarkMode ? "#e5e7eb" : "#111827" }}>
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </p>
                <p style={{ margin: 0, fontSize: 11, color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
                  {scheduledDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                  {" · "}
                  {scheduledDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                border: "none", background: isDarkMode ? "#374151" : "#f3f4f6",
                borderRadius: 8, width: 28, height: 28, cursor: "pointer",
                color: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 16, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >×</button>
          </div>

          <span style={{
            display: "inline-block", marginBottom: 14,
            fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 99,
            background: post.status === "scheduled"
              ? (isDarkMode ? "#713f12" : "#fef3c7")
              : post.status === "failed"
                ? (isDarkMode ? "#7f1d1d" : "#fee2e2")
                : (isDarkMode ? "#14532d" : "#dcfce7"),
            color: post.status === "scheduled"
              ? (isDarkMode ? "#fcd34d" : "#92400e")
              : post.status === "failed"
                ? (isDarkMode ? "#fca5a5" : "#b91c1c")
                : (isDarkMode ? "#86efac" : "#166534"),
          }}>
            {post.status === "scheduled" ? "⏰ Scheduled" : post.status === "failed" ? "⚠️ Failed" : "✓ Published"}
          </span>

          {post.status === "failed" && (
            <div
              style={{
                background: isDarkMode ? "#450a0a" : "#fef2f2",
                border: `1px solid ${isDarkMode ? "#7f1d1d" : "#fecaca"}`,
                borderRadius: 8, padding: "10px 12px", marginBottom: 14,
                fontSize: 13, lineHeight: 1.5,
                color: isDarkMode ? "#fca5a5" : "#991b1b",
              }}
            >
              <strong>This post wasn't published.</strong>{" "}
              {post.errorMessage || "The platform rejected the post. Please check your media and try again."}
            </div>
          )}

          <div
            style={{
              background: isDarkMode ? "#111827" : "#f9fafb",
              borderRadius: 8, padding: "12px 14px", marginBottom: 18,
              fontSize: 14, lineHeight: 1.6,
              color: isDarkMode ? "#d1d5db" : "#374151",
              maxHeight: 160, overflowY: "auto",
            }}
          >
            {post.body || post.caption || post.title || "No content"}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            {post.status === "scheduled" && (
              <button
                onClick={() => { onEdit && onEdit(post); onClose(); }}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                  background: isDarkMode ? "#1e40af" : "#3b82f6",
                  color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer",
                }}
              >✏️ Edit Post</button>
            )}
            {post.releaseURL && (
              <a
                href={post.releaseURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                  background: isDarkMode ? "#374151" : "#f3f4f6",
                  color: isDarkMode ? "#e5e7eb" : "#374151",
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                  textDecoration: "none", textAlign: "center",
                }}
              >👁️ View Live</a>
            )}
            {(post.status === "scheduled" || post.status === "failed") && (
              <button
                onClick={() => onDelete && onDelete(post)}
                disabled={isDeleting}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                  background: isDarkMode ? "#7f1d1d" : "#fee2e2",
                  color: isDarkMode ? "#fca5a5" : "#b91c1c",
                  fontWeight: 600, fontSize: 14,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  opacity: isDeleting ? 0.6 : 1,
                }}
              >{isDeleting ? "Deleting…" : "🗑️ Delete"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
// NEW PROP: `refreshTrigger` — increment this from the parent after a post is
// created/edited to force an immediate re-fetch.
// Example in parent:
//   const [calRefresh, setCalRefresh] = useState(0);
//   // after schedulePost / publishPost succeeds:
//   setCalRefresh(n => n + 1);
//   <PMScheduleCalendar ... refreshTrigger={calRefresh} />

const POLL_INTERVAL_MS = 30_000; // background poll every 30 s

export default function PMScheduleCalendar({ token, isDarkMode = false, onEditPost, refreshTrigger }) {
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPost, setSelectedPost] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const pollRef = useRef(null);

  const fetchScheduledPosts = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const result = await postizAPI.getScheduledPosts(token, { limit: 200 });
      setScheduledPosts(Array.isArray(result?.data) ? result.data : []);
    } catch {
      if (!silent) toast.error("Failed to load schedule");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Initial load + whenever token changes
  useEffect(() => {
    if (token) fetchScheduledPosts();
  }, [token]);

  // Re-fetch immediately whenever the parent signals a new post was created
  useEffect(() => {
    if (token && refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchScheduledPosts(true); // silent = no loading spinner
    }
  }, [refreshTrigger]);

  // Background polling so the calendar stays in sync automatically
  useEffect(() => {
    if (!token) return;
    pollRef.current = setInterval(() => fetchScheduledPosts(true), POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [token]);

  const handleDeletePost = async (post) => {
    if (!window.confirm(`Delete this post? This cannot be undone.`)) return;
    const idToDelete = post.group || post.id;
    setDeletingPostId(post.id);
    try {
      const result = await postizAPI.deletePost(token, idToDelete);
      if (result.success) {
        setScheduledPosts(prev => prev.filter(p => (p.group || p.id) !== idToDelete));
        setSelectedPost(null);
        toast.success("Post deleted");
        await fetchScheduledPosts(true);
      } else {
        toast.error(result.error || "Failed to delete post");
      }
    } catch {
      toast.error("Failed to delete post");
    } finally {
      setDeletingPostId(null);
    }
  };

  // ── Calendar helpers ──────────────────────────────────────────────────────
  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();
  const today       = new Date();

  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({ day: daysInPrev - i, currentMonth: false, date: new Date(year, month - 1, daysInPrev - i) });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ day: d, currentMonth: true, date: new Date(year, month, d) });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++)
    cells.push({ day: d, currentMonth: false, date: new Date(year, month + 1, d) });

  const getPostsForDate = (date) =>
    scheduledPosts.filter(post => {
      const pd = new Date(post.scheduledDate || post.createdAt);
      return pd.getFullYear() === date.getFullYear()
        && pd.getMonth() === date.getMonth()
        && pd.getDate() === date.getDate();
    });

  const isToday = (date) =>
    date.getDate() === today.getDate()
    && date.getMonth() === today.getMonth()
    && date.getFullYear() === today.getFullYear();

  const monthName   = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const navigateMonth = (dir) => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + dir, 1));
  const goToday     = () => setCurrentDate(new Date());

  // ── Stats ─────────────────────────────────────────────────────────────────
  const monthPosts = scheduledPosts.filter(p => {
    const d = new Date(p.scheduledDate || p.createdAt);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  const scheduledCount = monthPosts.filter(p => p.status === "scheduled").length;
  const publishedCount = monthPosts.filter(p => p.status === "published").length;
  const failedCount    = monthPosts.filter(p => p.status === "failed").length;
  const platformsSet   = new Set(scheduledPosts.map(p => p.platform || p.platforms?.[0]).filter(Boolean));

  // ── Styles ─────────────────────────────────────────────────────────────────
  const surface = isDarkMode ? "#111827" : "#f3f4f6";
  const card    = isDarkMode ? "#1f2937" : "#ffffff";
  const border  = isDarkMode ? "#374151" : "#e5e7eb";
  const textPri = isDarkMode ? "#f9fafb"  : "#111827";
  const textSec = isDarkMode ? "#9ca3af"  : "#6b7280";
  const accent  = "#3b82f6";

  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400, background: surface }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", margin: "0 auto 12px" }} />
          <p style={{ color: textSec, fontSize: 13 }}>Loading calendar…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const kpiStats = [
    { label: "Scheduled", value: scheduledCount, color: "#f59e0b", icon: "⏰" },
    { label: "Published",  value: publishedCount,  color: "#10b981", icon: "✓"  },
    ...(failedCount > 0 ? [{ label: "Failed", value: failedCount, color: "#ef4444", icon: "⚠️" }] : []),
    { label: "Platforms",  value: platformsSet.size, color: "#6366f1", icon: "🔗" },
  ];

  return (
    <div style={{ background: surface, borderRadius: 12, padding: 20, fontFamily: "system-ui, -apple-system, sans-serif" }}>

      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: textPri }}>Schedule Calendar</h2>
        <p style={{ margin: "2px 0 0", fontSize: 13, color: textSec }}>
          {monthPosts.length} post{monthPosts.length !== 1 ? "s" : ""} this month
        </p>
      </div>

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>

        {/* ── Left KPI column ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, flexShrink: 0, width: 130 }}>
          {kpiStats.map(s => (
            <div key={s.label} style={{
              background: card, borderRadius: 10, padding: "14px 12px",
              border: `1px solid ${border}`,
              borderLeft: `4px solid ${s.color}`,
            }}>
              <p style={{ margin: 0, fontSize: 26, fontWeight: 700, color: textPri, lineHeight: 1 }}>{s.value}</p>
              <p style={{ margin: "5px 0 0", fontSize: 11, color: textSec, display: "flex", alignItems: "center", gap: 4 }}>
                <span>{s.icon}</span> {s.label}
              </p>
            </div>
          ))}

          {platformsSet.size > 0 && (
            <div style={{ background: card, borderRadius: 10, padding: "12px", border: `1px solid ${border}`, marginTop: 4 }}>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 600, color: textSec }}>Platforms</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[...platformsSet].map(p => {
                  const { bg } = getPlatformColor(p);
                  return (
                    <span key={p} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: bg, display: "inline-block", flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: textPri, textTransform: "capitalize", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Calendar ── */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Nav bar */}
          <div style={{
            background: card, borderRadius: 10, padding: "8px 12px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 8, border: `1px solid ${border}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button onClick={() => navigateMonth(-1)} style={navBtn(isDarkMode)}>‹</button>
              <span style={{ fontSize: 14, fontWeight: 700, color: textPri, minWidth: 120, textAlign: "center" }}>
                {monthName}
              </span>
              <button onClick={() => navigateMonth(1)} style={navBtn(isDarkMode)}>›</button>
            </div>
            <button
              onClick={goToday}
              style={{
                padding: "4px 12px", borderRadius: 7, border: `1px solid ${border}`,
                background: "transparent", color: accent, fontWeight: 600, fontSize: 12,
                cursor: "pointer",
              }}
            >Today</button>
          </div>

          {/* Day-of-week headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 2 }}>
            {DOW.map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 600, color: textSec, padding: "2px 0" }}>
                {d}
              </div>
            ))}
          </div>

          {/* Grid — gap reduced from 3→2 */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((cell, idx) => {
              const posts = getPostsForDate(cell.date);
              return (
                <CalendarCell
                  key={idx}
                  day={cell.day}
                  posts={posts}
                  isCurrentMonth={cell.currentMonth}
                  isToday={isToday(cell.date)}
                  isDarkMode={isDarkMode}
                  onPostClick={setSelectedPost}
                  onEditPost={post => onEditPost && onEditPost(post)}
                  onDeletePost={handleDeletePost}
                  deletingPostId={deletingPostId}
                />
              );
            })}
          </div>
        </div>
      </div>

      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          isDarkMode={isDarkMode}
          onClose={() => setSelectedPost(null)}
          onEdit={post => { onEditPost && onEditPost(post); setSelectedPost(null); }}
          onDelete={handleDeletePost}
          isDeleting={deletingPostId === selectedPost.id}
        />
      )}
    </div>
  );
}

function navBtn(isDarkMode) {
  return {
    width: 28, height: 28, borderRadius: 7,
    border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
    background: "transparent",
    color: isDarkMode ? "#e5e7eb" : "#374151",
    fontSize: 18, lineHeight: 1, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 300,
  };
}
